"use client";

import React from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import SubDrawer from "./SubDrawer";
import DrawerItem from "./DrawerItem";
import { useAuthUser } from "@/lib/auth";
import { useBookmarkCount } from "@/lib/bookmarks";
// use string paths for images to avoid TS module declarations for svgs
const IconClose = "/assets/icons/icon-close.svg";
const BackArrow = "/assets/icons/back-arrow.svg";
// LoginForm is shown inside SubDrawer now

import type { NavbarLink, NavbarAction, SubDrawer as SubDrawerCfg } from "../navbarData";
import Link from "next/dist/client/link";

type Props = {
  open: boolean;
  links: NavbarLink[];
  actions: NavbarAction[];
  subDrawers: Record<string, SubDrawerCfg>;
  activeSubDrawer: string | null;
  onClose: () => void;
  onOpenSubDrawer: (id: string) => void;
  onCloseSubDrawer: () => void;
};

export default function Drawer({
  open,
  links,
  actions,
  subDrawers,
  activeSubDrawer,
  onClose,
  onOpenSubDrawer,
  onCloseSubDrawer,
}: Props) {
  // account view is handled via subdrawer; no local accountOpen state
  const router = useRouter();
  const user = useAuthUser();
  const bookmarkCount = useBookmarkCount();

  return (
    <div
      className={`navbar__drawer fixed left-0 right-0 h-screen bg-[#ffffff] z-[50] transform transition-all duration-300 ease-in-out flex flex-col ${open
        ? "top-0 translate-y-0 opacity-100"
        : "top-full translate-y-0 opacity-0 pointer-events-none"
        }`}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
    >
      <div className="navbar__drawer-header flex flex-col items-center gap-[17px] md:p-[24px_20px_8px_20px] p-[18px_16px_8px_16px] bg-white">
        <Link href="/" className="w-full flex items-center justify-center" aria-label="Retour à l'accueil">
          <Image
            className="navbar__drawer-logo w-64.5 h-fit object-contain"
            src="/assets/logo-blue.svg"
            alt="Spot Formations"
            title="Spot Formations"
            width={258}
            height={17.49}
          />
        </Link>

        <div className="navbar__drawer-meta relative w-full flex items-center justify-center">
          {Boolean(activeSubDrawer) && (
            <button
              className="navbar__drawer-back absolute left-0 bg-transparent border-none text-[22px] text-[#1b365e] cursor-pointer"
              onClick={() => {
                onCloseSubDrawer();
              }}
              aria-label="Retour"
              type="button"
            >
              <Image
                src={BackArrow}
                alt="Retour"
                title="Retour"
                width={24}
                height={24}
                draggable="false"
                className="w-[20px] h-[20px] object-contain block pointer-events-none"
              />
            </button>
          )}

          <h2 className="navbar__drawer-title m-0 text-[#1b365e] font-light font-red-hat text-[18px] leading-5 tracking-[2%]">
            {activeSubDrawer ? subDrawers[activeSubDrawer as string]?.title || 'Menu' : 'Menu'}
          </h2>

          <button
            className="navbar__drawer-close absolute right-0 bg-transparent border-none text-[22px] text-[#1b365e] cursor-pointer"
            onClick={onClose}
            aria-label="Fermer le menu"
            type="button"
          >
            <Image
              src={IconClose}
              width={24}
              height={24}
              alt="Fermer"
              title="Fermer"
              draggable="false"
              className="w-[24px] h-[24px] object-contain block pointer-events-none"
            />
          </button>
        </div>
      </div>

      <div className="navbar__drawer-content flex-1 md:px-5 px-4 py-6.5 overflow-y-auto flex flex-col">
        <>
          <ul className="navbar__drawer-list list-none m-0 p-0 flex flex-col gap-4.5">
            <li>
              <DrawerItem
                link={{ href: "/", label: "Accueil", variant: "highlight" }}
              />
            </li>
            {links.map((link) => {
              const { label, subDrawerId } = link;
              const isSubDrawer = Boolean(subDrawerId);

              return (
                <li key={label}>
                  <DrawerItem
                    link={link}
                    onClick={isSubDrawer ? () => onOpenSubDrawer(subDrawerId as string) : undefined}
                  />
                </li>
              );
            })}
          </ul>

          <div className="navbar__drawer-sections flex flex-col gap-6 mt-4">
            {actions.map(({ key, label, variant }) => (
              <button
                key={key}
                type="button"
                className={`drawer-section ${variant === "account" ? "mt-[40px] p-[10px_14px_10px_18px]" : " p-[0_14px_0_0]"} flex items-center justify-between gap-[12px] rounded-[58px] h-[38px] ${variant === "account" || variant === "bookmark" ? "border-[#ffab5b] text-[#fc7f16]" : "border-[#f2f2f2]"
                  } ${variant === "secondary"
                    ? "bg-[#EFEFEF] text-[#1B515E]"
                    : variant === "highlight_two"
                      ? "bg-[#EFEFEF] text-[#1B365E]"
                      : variant === "contact"
                        ? "bg-[#FFE4B4] text-[#1b365e]"
                        : "bg-[#ffffff] text-[#1b365e]"
                  } font-bold md:text-[15px] text-[12px] shadow-[0_12px_22px_rgba(15,38,107,0.06)] cursor-pointer text-left transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_16px_28px_rgba(15,38,107,0.12)]`}
                style={
                  variant === "account" || variant === "bookmark"
                    ? {
                      backgroundImage:
                        "linear-gradient(white, white), linear-gradient(180deg,#ffbc41 26%,#ff5758 100%)",
                      backgroundOrigin: "border-box",
                      backgroundClip: "padding-box, border-box",
                      border: "1px solid transparent",
                    }
                    : undefined
                }
                onClick={() => {
                  if (variant === "account") {
                    if (user) {
                      onOpenSubDrawer('account');
                    } else {
                      onClose();
                      router.push('/login');
                    }
                  } else if (key === "bookmarks") {
                    onClose();
                    router.push('/favoris');
                  }
                }}
              >
                <div className={`drawer-section__icon rounded-full flex items-center justify-center mr-[12px] ${variant === "account" ? "w-[19px] h-[16.19px]" : "flex min-w-[38px] min-h-[38px] border border-[#FC7F16] ml-[-1px]"}`}>
                  {variant === "account" ? (
                    <Image className="w-[19px] h-[16.19px]" src="/assets/icons/icon-monCompte.svg" alt="Mon compte" title="Mon compte" width={25} height={25} />
                  ) : (
                    <Image className="w-4.5 h-4.5" src="/assets/icons/icon-bookmark.svg" alt="Favoris" title="Favoris" width={25} height={25} />
                  )}
                </div>
                <span className={`drawer-section__label flex-1 ${variant === "bookmark" && "py-2.5"}`}>
                  {key === "bookmarks" ? `${label} (${bookmarkCount})` : label}
                </span>
                <span className="drawer-section__arrow ml-[12px]" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1L7 7L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            ))}
          </div>
        </>
      </div>

      <div className="navbar__drawer-footer pt-2 pb-6 px-4 bg-[#1b365e] text-[#ffffff] flex flex-col gap-8">
        <div className="drawer-footer-row flex items-center justify-center gap-6 pb-3 border-b border-[#C7F8FF]">
          <span className="drawer-footer-text text-[8px] leading-5 text-[#d9d9d9] tracking-[0.02em]">
            Vous avez une question ? <span className="mx-1">(de 8h à 18h) </span>
          </span>
          <span className="drawer-phone inline-flex items-center gap-[6px] font-bold text-[8px]">
            <Image
              src="/assets/icons/phone.svg"
              alt="Téléphone"
              title="Téléphone"
              width={14}
              height={14}
            />
            + 33 6 00 00 00 00
          </span>
        </div>

        {/* <div className="drawer-footer-divider w-full h-[1px] bg-[rgba(255,255,255,0.35)]"></div> */}

        <button
          type="button"
          className="drawer-lang flex items-center gap-2.5 cursor-pointer"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1049_13318)">
              <path d="M16.5 12C16.5 13.0406 16.4438 14.0437 16.3453 15H7.65937C7.55625 14.0437 7.50469 13.0406 7.50469 12C7.50469 10.9594 7.56094 9.95625 7.65937 9H16.3453C16.4484 9.95625 16.5 10.9594 16.5 12ZM17.85 9H23.6203C23.8687 9.96094 24 10.9641 24 12C24 13.0359 23.8687 14.0391 23.6203 15H17.85C17.9484 14.0344 18 13.0312 18 12C18 10.9688 17.9484 9.96562 17.85 9ZM23.1281 7.5H17.6578C17.1891 4.50469 16.2609 1.99688 15.0656 0.39375C18.7359 1.36406 21.7219 4.02656 23.1234 7.5H23.1281ZM16.1391 7.5H7.86094C8.14687 5.79375 8.5875 4.28438 9.12656 3.06094C9.61875 1.95469 10.1672 1.15313 10.6969 0.646875C11.2219 0.15 11.6578 0 12 0C12.3422 0 12.7781 0.15 13.3031 0.646875C13.8328 1.15313 14.3812 1.95469 14.8734 3.06094C15.4172 4.27969 15.8531 5.78906 16.1391 7.5ZM6.34219 7.5H0.871875C2.27813 4.02656 5.25938 1.36406 8.93438 0.39375C7.73906 1.99688 6.81094 4.50469 6.34219 7.5ZM0.379688 9H6.15C6.05156 9.96562 6 10.9688 6 12C6 13.0312 6.05156 14.0344 6.15 15H0.379688C0.13125 14.0391 0 13.0359 0 12C0 10.9641 0.13125 9.96094 0.379688 9ZM9.12656 20.9344C8.58281 19.7156 8.14687 18.2062 7.86094 16.5H16.1391C15.8531 18.2062 15.4125 19.7156 14.8734 20.9344C14.3812 22.0406 13.8328 22.8422 13.3031 23.3484C12.7781 23.85 12.3422 24 12 24C11.6578 24 11.2219 23.85 10.6969 23.3531C10.1672 22.8469 9.61875 22.0453 9.12656 20.9391V20.9344ZM6.34219 16.5C6.81094 19.4953 7.73906 22.0031 8.93438 23.6063C5.25938 22.6359 2.27813 19.9734 0.871875 16.5H6.34219ZM23.1281 16.5C21.7219 19.9734 18.7406 22.6359 15.0703 23.6063C16.2656 22.0031 17.1891 19.4953 17.6625 16.5H23.1328H23.1281Z" fill="#C7F8FF" />
            </g>
            <defs>
              <clipPath id="clip0_1049_13318">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>

          <span className="text-[#C7F8FF] text-[12px] leading-5 font-normal">Français (FR)</span>
        </button>
      </div>

      {Boolean(activeSubDrawer) && (
        <SubDrawer
          config={subDrawers[activeSubDrawer as string]}
          isOpen={true}
          onBack={onCloseSubDrawer}
          onClose={onClose}
          onOpenSubDrawer={onOpenSubDrawer}
        />
      )}
    </div>
  );
}
